/**
 * ViewportMessages — floating message pills inside the camera frame.
 *
 * Rendered as a child of the camera frame overlay div, so positioning
 * is relative to the frame bounds (not the full viewport).
 *
 * Three severity levels:
 *   - **info** (blue) — active operators that change input behavior
 *   - **warning** (amber) — behavior modifiers that alter how tools operate
 *   - **error** (red) — invalid states that block operations
 *
 * Pills with an `onDismiss` callback render a small × button on the right.
 *
 * Operator metadata comes from the command metadata registry. Each command
 * type specifies its display label and an optional hint shown while active.
 */

import {
  ArrowRight,
  CircleDot,
  Info,
  Magnet,
  Navigation,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useStore } from "../app/store.ts";
import { getCommandMetadata } from "../modeling/operatorParams.ts";
import { exitAxisView } from "../viewport/cameraViews.ts";
import { mergeModifierStack } from "../viewport/modifiers/evaluation.ts";
import { viewLabel } from "../viewport/viewLock.ts";
import { IconButton } from "./Button.tsx";
import { ViewAxisIcon } from "./icons3d.tsx";
import {
  COLOR_ACCENT,
  COLOR_ERROR,
  COLOR_ERROR_TINT_BG,
  COLOR_ERROR_TINT_BORDER,
  COLOR_INFO_TINT_BG,
  COLOR_INFO_TINT_BORDER,
  COLOR_WARNING,
  COLOR_WARNING_TINT_BG,
  COLOR_WARNING_TINT_BORDER,
  FONT_SIZE_SMALL,
  RADIUS_CONTROL,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Severity system
// ---------------------------------------------------------------------------

type Severity = "info" | "warning" | "error";

const SEVERITY_STYLE: Record<
  Severity,
  {
    color: string;
    background: string;
    border: string;
  }
> = {
  info: {
    color: COLOR_ACCENT,
    background: COLOR_INFO_TINT_BG,
    border: COLOR_INFO_TINT_BORDER,
  },
  warning: {
    color: COLOR_WARNING,
    background: COLOR_WARNING_TINT_BG,
    border: COLOR_WARNING_TINT_BORDER,
  },
  error: {
    color: COLOR_ERROR,
    background: COLOR_ERROR_TINT_BG,
    border: COLOR_ERROR_TINT_BORDER,
  },
};

// ---------------------------------------------------------------------------
// Message type
// ---------------------------------------------------------------------------

export interface ViewportMessage {
  key: string;
  severity: Severity;
  icon: ReactNode;
  text: ReactNode;
  /** If provided, renders a dismiss × button that calls this callback. */
  onDismiss?: () => void;
}

// ---------------------------------------------------------------------------
// Pill renderer
// ---------------------------------------------------------------------------

function MessagePill({ message }: { message: ViewportMessage }) {
  const s = SEVERITY_STYLE[message.severity];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 10px",
        borderRadius: RADIUS_CONTROL,
        background: s.background,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `0.5px solid ${s.border}`,
        color: s.color,
        fontSize: FONT_SIZE_SMALL,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "100%",
        pointerEvents: message.onDismiss ? "auto" : "none",
      }}
    >
      {message.icon}
      {message.text}
      {message.onDismiss && (
        <IconButton
          onClick={message.onDismiss}
          icon={<X size={10} />}
          label="Dismiss message"
          variant="ghost"
          size="xs"
          style={{
            width: 16,
            height: 16,
            padding: 0,
            color: "inherit",
            opacity: 0.8,
            flexShrink: 0,
            marginLeft: 2,
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Axis helpers
// ---------------------------------------------------------------------------

const AXIS_LABEL: Record<string, string> = {
  x: "X axis",
  y: "Y axis",
  z: "Z axis",
  normal: "Normal",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ViewportMessages() {
  const commandBuffer = useStore((s) => s.commandBuffer);
  const pendingAxis = useStore((s) => s.pendingAxis);
  const proportionalEnabled = useStore((s) => s.proportionalEnabled);
  const proportionalRadius = useStore((s) => s.proportionalRadius);
  const proportionalFalloff = useStore((s) => s.proportionalFalloff);
  const snapEnabled = useStore((s) => s.snapEnabled);
  const snapTarget = useStore((s) => s.snapTarget);
  const snapWithRotation = useStore((s) => s.snapWithRotation);
  const surfaceProjectIndividual = useStore((s) => s.surfaceProjectIndividual);
  const editMode = useStore((s) => s.editMode);
  const appMode = useStore((s) => s.mode);
  const viewLock = useStore((s) => s.viewLock);
  const sculptMode = useStore((s) => s.sculptMode);
  const modifierOverrides = useStore((s) => s.modifierOverrides);

  const clearAxisConstraint = useCallback(() => {
    const cb = useStore.getState().commandBuffer;
    if (cb) {
      useStore.setState({ commandBuffer: { ...cb, axis: null } });
    }
    useStore.setState({ pendingAxis: null });
  }, []);

  const disableProportional = useCallback(() => {
    useStore.getState().setProportionalEnabled(false);
  }, []);

  const disableSnap = useCallback(() => {
    useStore.getState().setSnapEnabled(false);
  }, []);

  const exitFlyMode = useCallback(() => {
    useStore.setState({ flyMode: false });
  }, []);

  const exitViewLock = useCallback(() => {
    exitAxisView();
  }, []);

  const messages: ViewportMessage[] = [];

  // --- Fly mode pill ---
  const flyMode = useStore((s) => s.flyMode);
  if (flyMode) {
    messages.push({
      key: "flymode",
      severity: "info",
      icon: <Navigation size={12} style={{ flexShrink: 0 }} />,
      text: (
        <span>
          <strong>Fly Mode</strong> — WASD move, mouse look, scroll focal
          length, Shift fast, Esc exit
        </span>
      ),
      onDismiss: exitFlyMode,
    });
  }

  // --- Operator info pill ---
  if (commandBuffer) {
    const meta = getCommandMetadata(commandBuffer.type);
    if (meta.hint) {
      // Show active value mode label if a non-primary param is selected
      let hint = meta.hint;
      if (commandBuffer.activeValueMode) {
        const paramDef = meta.params.find(
          (p) => p.key === commandBuffer.activeValueMode,
        );
        if (paramDef) hint = `Drag to adjust ${paramDef.label.toLowerCase()}`;
      }
      messages.push({
        key: "operator",
        severity: "info",
        icon: <Info size={12} style={{ flexShrink: 0 }} />,
        text: (
          <span>
            <strong>{meta.label}</strong> — {hint}
          </span>
        ),
      });
    }
  }

  // --- Axis-aligned view lock pill ---
  if (viewLock) {
    const label = viewLabel(viewLock.axis, viewLock.sign);
    messages.push({
      key: "view-lock",
      severity: "warning",
      icon: <ViewAxisIcon size={12} style={{ flexShrink: 0 }} />,
      text: (
        <span>
          View: <strong>{label}</strong>
        </span>
      ),
      onDismiss: exitViewLock,
    });
  }

  // --- Axis constraint pill ---
  const activeAxis = commandBuffer?.axis ?? pendingAxis;
  if (activeAxis) {
    const label = AXIS_LABEL[activeAxis] ?? activeAxis;
    messages.push({
      key: "axis-constraint",
      severity: "warning",
      icon: <ArrowRight size={12} style={{ flexShrink: 0 }} />,
      text: (
        <span>
          Constrained to <strong>{label}</strong>
        </span>
      ),
      onDismiss: clearAxisConstraint,
    });
  }

  // --- Proportional editing pill ---
  const inEditMode = appMode === "edit" && editMode != null;
  if (proportionalEnabled && inEditMode) {
    messages.push({
      key: "proportional",
      severity: "warning",
      icon: <CircleDot size={12} style={{ flexShrink: 0 }} />,
      text: (
        <span>
          Proportional <strong>{proportionalFalloff}</strong> r=
          {Math.round(proportionalRadius)}px
        </span>
      ),
      onDismiss: disableProportional,
    });
  }

  // --- Snap pill ---
  if (snapEnabled) {
    const snapBaseLabel =
      snapTarget === "surface" && inEditMode && surfaceProjectIndividual
        ? "surface + conform"
        : snapTarget;
    const snapLabel = snapWithRotation
      ? `${snapBaseLabel} + rotation`
      : snapBaseLabel;
    messages.push({
      key: "snap",
      severity: "warning",
      icon: <Magnet size={12} style={{ flexShrink: 0 }} />,
      text: (
        <span>
          Snap <strong>{snapLabel}</strong>
        </span>
      ),
      onDismiss: disableSnap,
    });
  }

  // Sculpt is per-object — modifier suspension applies to the sculpt
  // target. Sketch is global and modifiers are suspended scene-wide while
  // sketch mode is active, so reporting a single object's suspended-count
  // would be misleading; skip the message in sketch mode.
  const modifierSuspension = sculptMode
    ? { mode: "Sculpt", objectId: sculptMode.objectId }
    : null;
  if (modifierSuspension) {
    const merged = mergeModifierStack(
      [],
      modifierOverrides.get(modifierSuspension.objectId) ?? [],
    );
    const suspendedCount = merged.filter(
      (modifier) => modifier.enabled && modifier.kind !== "followObject",
    ).length;
    if (suspendedCount > 0) {
      messages.push({
        key: "modifier-suspension",
        severity: "warning",
        icon: <Info size={12} style={{ flexShrink: 0 }} />,
        text: (
          <span>
            <strong>{modifierSuspension.mode}</strong> uses the base cage;
            geometry modifiers are suspended
          </span>
        ),
      });
    }
  }

  if (messages.length === 0) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: 8,
        right: 48,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 6,
        pointerEvents: "none",
      }}
    >
      {messages.map((msg) => (
        <MessagePill key={msg.key} message={msg} />
      ))}
    </div>
  );
}
